import {
  api,
  getStaticPathsWithStore,
  storeWrapper,
} from "@chef/state-management";
import { capitalize } from "@chef/utils/string";
import { IRecipesCategory, getRecipesCategoryData } from "@chef/feature-sanity";
import { BRAND_PATHS, language } from "@chef/constants";
import { isNotEmptyArray } from "@chef/utils/array";

import { Tree } from "./utils/tree";

export * from "./utils/pagination";

export * from "./pages/CategoryPage";
export * from "./pages/CategoriesPage";
export * from "./pages/CategoryPage.Intl";

type Key = string | number;

type Paths = { params: { categoryPath: string[] } }[];

export const CategoriesPageGetStaticProps = storeWrapper.getStaticProps<{
  categories: {
    parent: { value: string; slug: string };
    children: { value: string; slug: string }[];
  }[];
  allCategories?: IRecipesCategory[];
  breadcrumbs: { value: string; slug: string }[];
}>((store) => async () => {
  const rootSlug = intl.RECIPES;

  const { getIngredientCategoriesByCompany } = await store
    .dispatch(api.endpoints.ingredientCategoriesByCompany.initiate())
    .unwrap();

  const tree = new Tree(rootSlug, capitalize(intl.RECIPES));

  const ingredientCategories = [...getIngredientCategoriesByCompany].sort(
    (a, b) => a.ancestorIds.length - b.ancestorIds.length,
  );

  for (const ingredientCategory of ingredientCategories) {
    tree.insertAndCoalesceByValue(
      ingredientCategory.parentIngredientCategoryId,
      ingredientCategory.ingredientCategoryId,
      ingredientCategory.ingredientCategoryName,
      rootSlug,
    );
  }

  const categoriesWithRecipes = [];

  for (const parentNode of tree.root.children) {
    const parentCategoryIds = parentNode.ids as number[];

    const parentRecipesResponse = await store
      .dispatch(
        api.endpoints.recipesByIngredientCategories.initiate({
          ingredientCategoryIds: parentCategoryIds,
          pageNumber: 1,
          resultsPerPage: 1, // Only need 1 recipe to check if recipes exist
        }),
      )
      .unwrap();

    const parentHasRecipes = isNotEmptyArray(
      parentRecipesResponse.getRecipesByIngredientCategories.recipes,
    );

    if (parentHasRecipes) {
      const childrenWithRecipes = [];

      for (const childNode of parentNode.children) {
        const childCategoryIds = childNode.ids as number[];

        const childRecipesResponse = await store
          .dispatch(
            api.endpoints.recipesByIngredientCategories.initiate({
              ingredientCategoryIds: childCategoryIds,
              pageNumber: 1,
              resultsPerPage: 1, // Only need 1 recipe to check if recipes exist
            }),
          )
          .unwrap();

        const childHasRecipes = isNotEmptyArray(
          childRecipesResponse.getRecipesByIngredientCategories.recipes,
        );

        if (childHasRecipes) {
          childrenWithRecipes.push({
            value: childNode.value.toString(),
            slug: childNode.slug,
          });
        }
      }

      categoriesWithRecipes.push({
        parent: {
          value: parentNode.value.toString(),
          slug: parentNode.slug,
        },
        children: childrenWithRecipes,
      });
    }
  }

  const breadcrumbs = [
    { value: capitalize(intl.RECIPES), slug: BRAND_PATHS.RECIPE_HUB_PATH },
    {
      value: capitalize(intl.CATEGORIES),
      slug: BRAND_PATHS.RECIPE_HUB_CATEGORIES_PATH,
    },
  ];

  return {
    props: {
      categories: categoriesWithRecipes,
      breadcrumbs,
    },
    revalidate: 60 * 60 * 24, // 1 day
  };
});

export const CategoryPageGetStaticProps = storeWrapper.getStaticProps<{
  rootCategories: { value: Key; slug: string }[];
  breadcrumbs: { value: Key; slug: string }[];
  ingredientCategoryIds: number | number[] | undefined;
  recipesCategory?: IRecipesCategory;
  totalPages: number;
  page: number;
}>((store) => async (ctx) => {
  const categoryPath = ctx.params?.["categoryPath"];

  const rootSlug = intl.RECIPES;

  let slug: typeof intl.RECIPES | string = rootSlug;
  let page = 1;

  // Check if last element in path is a page number
  if (
    Array.isArray(categoryPath) &&
    /^\d+$/.test(categoryPath[categoryPath.length - 1])
  ) {
    const p = categoryPath.pop();
    page = +(p ?? 1);
  }

  if (Array.isArray(categoryPath) && categoryPath.length > 0) {
    slug = categoryPath.join("/");
  }

  const { getIngredientCategoriesByCompany } = await store
    .dispatch(api.endpoints.ingredientCategoriesByCompany.initiate())
    .unwrap();

  const tree = new Tree(rootSlug, capitalize(intl.RECIPES));

  const ingredientCategories = [...getIngredientCategoriesByCompany].sort(
    (a, b) => a.ancestorIds.length - b.ancestorIds.length,
  );

  for (const ingredientCategory of ingredientCategories) {
    tree.insertAndCoalesceByValue(
      ingredientCategory.parentIngredientCategoryId,
      ingredientCategory.ingredientCategoryId,
      ingredientCategory.ingredientCategoryName,
      rootSlug,
    );
  }

  const node = tree.findBySlug(slug, rootSlug);

  if (!node) {
    return {
      props: {
        rootCategories: [],
        ingredientCategoryIds: [],
        breadcrumbs: [],
        page: null,
      },
      notFound: true,
    };
  }

  const ingredientCategoryIds =
    node.key === rootSlug
      ? (tree.root.children.flatMap((child) => child.ids) as number[])
      : (node.ids as number[]);

  const rootCategoriesWithRecipes = [];

  for (const child of node.children) {
    const childCategoryIds = child.ids as number[];

    if (childCategoryIds.length > 0) {
      const recipesForChildCategory = await store
        .dispatch(
          api.endpoints.recipesByIngredientCategories.initiate({
            ingredientCategoryIds: childCategoryIds,
            pageNumber: 1,
            resultsPerPage: 1, // Only need 1 recipe to check if recipes exist
          }),
        )
        .unwrap();

      const recipes =
        recipesForChildCategory.getRecipesByIngredientCategories?.recipes || [];

      if (isNotEmptyArray(recipes)) {
        rootCategoriesWithRecipes.push({
          value: child.value,
          slug: child.slug,
        });
      }
    }
  }

  const recipesByIngredientCategories = await store
    .dispatch(
      api.endpoints.recipesByIngredientCategories.initiate({
        ingredientCategoryIds,
        pageNumber: page,
        resultsPerPage: 30,
      }),
    )
    .unwrap();

  const totalPages =
    recipesByIngredientCategories.getRecipesByIngredientCategories.totalPages;

  const recipesCategoryContent = await getRecipesCategoryData(node.slug, {
    preview: ctx.preview,
  });

  await Promise.all(store.dispatch(api.util.getRunningQueriesThunk()));

  if (page === 0 || page > totalPages) {
    const destination =
      slug === rootSlug
        ? BRAND_PATHS.RECIPE_HUB_PATH
        : `${BRAND_PATHS.RECIPE_HUB_PATH}/${slug}`;

    return {
      redirect: {
        destination,
        statusCode: 307,
      },
    };
  }

  return {
    props: {
      rootCategories: rootCategoriesWithRecipes,
      ingredientCategoryIds,
      breadcrumbs: node.ancestors.map((a) => ({
        slug: a.slug,
        value: a.value,
      })),
      recipesCategory: recipesCategoryContent || null,
      totalPages,
      page,
      draftMode: ctx.preview || false,
    },
    revalidate: 60 * 60 * 24, // 1 day
  };
});

export const CategoryPageGetStaticPaths = getStaticPathsWithStore(
  (store) => async () => {
    const { getIngredientCategoriesByCompany } = await store
      .dispatch(api.endpoints.ingredientCategoriesByCompany.initiate())
      .unwrap();

    const rootSlug = intl.RECIPES;
    const tree = new Tree(rootSlug, capitalize(rootSlug));

    const ingredientCategories = [...getIngredientCategoriesByCompany].sort(
      (a, b) => a.ancestorIds.length - b.ancestorIds.length,
    );

    for (const ingredientCategory of ingredientCategories) {
      tree.insertAndCoalesceByValue(
        ingredientCategory.parentIngredientCategoryId,
        ingredientCategory.ingredientCategoryId,
        ingredientCategory.ingredientCategoryName,
        rootSlug,
      );
    }

    const paths: Paths = [{ params: { categoryPath: [] } }];

    // Skip pre-rendering if it's a preview deployment
    if (
      process.env.VERCEL_ENV === "preview" &&
      process.env.VERCEL_GIT_COMMIT_REF !== "qa"
    ) {
      return {
        paths,
        fallback: "blocking",
      };
    }

    for (const path of tree.paths) {
      const categoryPath = path.map((p) => p.toString());
      const node = tree.findBySlug(categoryPath.join("/"), rootSlug);

      if (node) {
        const ingredientCategoryIds =
          node.key === rootSlug
            ? (tree.root.children.flatMap((child) => child.ids) as number[])
            : (node.ids as number[]);

        if (isNotEmptyArray(ingredientCategoryIds)) {
          const { getRecipesByIngredientCategories } = await store
            .dispatch(
              api.endpoints.recipesByIngredientCategories.initiate({
                ingredientCategoryIds,
                pageNumber: 1,
                resultsPerPage: 30,
              }),
            )
            .unwrap();

          const totalPages = getRecipesByIngredientCategories.totalPages;

          // Pre-render first 2 pages
          for (let i = 1; i <= Math.min(totalPages, 2); i++) {
            if (i === 1) {
              paths.push({
                params: {
                  categoryPath,
                },
              });
            } else {
              paths.push({
                params: {
                  categoryPath: [...categoryPath, i.toString()],
                },
              });
            }
          }
        }
      }
    }

    return {
      paths,
      fallback: "blocking",
    };
  },
);

const intl = (
  {
    no: {
      RECIPES: "oppskrifter",
      CATEGORIES: "kategorier",
    },
    se: {
      RECIPES: "receptbank",
      CATEGORIES: "kategorier",
    },
    dk: {
      RECIPES: "opskrifter",
      CATEGORIES: "kategorier",
    },
  } as const
)[language];
